// ── Sales Excel import ───────────────────────────────────────────────────
// Reads a sheet of sale lines (one row per stone, rows sharing a Sale
// reference become one sale), checks each against the customer list and
// the stock on hand, then hands the sales to the Sales page one at a time.
// Each one opens the normal Add Sale form already filled in and is saved
// from there, so stock splits, memos, the Dummy code, brokers and the books
// all go through the one save path the form already has.

import { useMemo, useRef, useState } from 'react';
import { Button, Modal } from '../components/ui';
import type { Entity, InventoryItem } from '../types';
import {
  downloadImportWorkbook, loadWorkbook, readSheetRows, parseImportDate, cleanNum,
  xlsxUtcDate, XLSX_GROUP_ARGB, type XlsxCol,
} from '../utils/excelImport';
import { sellableByCode, availableCaratOf as availableCarat } from '../utils/saleImport';

type SaleField = 'sale' | 'saleDate' | 'customer' | 'terms' | 'keep' | 'broker' | 'brokerPct'
  | 'code' | 'stone' | 'carat' | 'pricePerCt' | 'amount';

const SALE_COLS: XlsxCol<SaleField>[] = [
  { header: 'Sale',          field: 'sale',       width: 12, headerArgb: XLSX_GROUP_ARGB, aliases: ['sale no', 'sale ref', 'invoice', 'ref'], note: 'Rows with the same Sale make one invoice.' },
  { header: 'Sale Date',     field: 'saleDate',   width: 13, headerArgb: XLSX_GROUP_ARGB, date: true, aliases: ['date'] },
  { header: 'Customer',      field: 'customer',   width: 30, headerArgb: XLSX_GROUP_ARGB, list: 'customers', aliases: ['company name', 'company', 'to'] },
  { header: 'Payment Terms', field: 'terms',      width: 14, headerArgb: XLSX_GROUP_ARGB, numeric: true, aliases: ['terms'], note: 'Days. Blank = the customer\'s default terms.' },
  { header: 'Keep (%)',      field: 'keep',       width: 10, headerArgb: XLSX_GROUP_ARGB, numeric: true, aliases: ['keep', 'keep %', 'less %', 'less (%)'] },
  { header: 'Broker',        field: 'broker',     width: 24, list: 'brokers' },
  { header: 'Broker %',      field: 'brokerPct',  width: 10, numeric: true, aliases: ['commission %', 'broker (%)'] },
  { header: 'Code',          field: 'code',       width: 11 },
  { header: 'Stone',         field: 'stone',      width: 32, aliases: ['description'], note: 'Just for you. Not read on import.' },
  { header: 'Carat to sell', field: 'carat',      width: 13, numeric: true, aliases: ['carat', 'ct'] },
  { header: 'Price/ct',      field: 'pricePerCt', width: 12, numeric: true, aliases: ['price per ct', 'price'] },
  { header: 'Amount',        field: 'amount',     width: 14, numeric: true, aliases: ['total', 'total amount'],
    formula: (r, col) => `IF(AND(${col('carat')}${r}<>"",${col('pricePerCt')}${r}<>""),${col('carat')}${r}*${col('pricePerCt')}${r},"")` },
];

export interface SaleImportLine {
  rowNo:       number;
  code:        string;
  carat:       number;
  pricePerCt:  number;
  brokerId?:   string;
  brokerPct?:  number;
}

export interface SaleImportGroup {
  key:          string;
  ref:          string;
  customerId:   string;
  customerName: string;
  saleDate:     string;
  terms:        number;
  keep:         number;
  lines:        SaleImportLine[];
  /** Things that stop this sale from filling in properly. */
  problems:     string[];
}

const norm = (s: string | undefined) => (s ?? '').trim().toLowerCase();
const r2 = (n: number) => Math.round(n * 100) / 100;


function IconUploadFile() {
  return <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
    <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8l-5-5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M14 3v5h5M12 17v-6M9.5 13.5L12 11l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

async function downloadSalesImportSample(entities: Entity[]) {
  const customers = entities.filter(e => e.types.includes('customer')).map(e => e.companyName).filter(Boolean).sort();
  const brokers   = entities.filter(e => e.types.includes('broker')).map(e => e.companyName).filter(Boolean).sort();
  const cust = customers[0] ?? 'Your Customer LLC';
  await downloadImportWorkbook<SaleField>({
    fileName: 'DiaWolt-sales-import-sample.xlsx',
    sheets: [{
      name: 'Sales', tabArgb: XLSX_GROUP_ARGB, cols: SALE_COLS,
      sample: [
        { sale: 'Sale 1', saleDate: xlsxUtcDate('2026-10-01'), customer: cust, terms: 30, keep: 0, code: '20001', stone: 'Round 0.30 ct parcel', carat: 5.2, pricePerCt: 950 },
        { sale: 'Sale 1', saleDate: xlsxUtcDate('2026-10-01'), customer: cust, terms: 30, keep: 0, code: '20002', stone: 'GIA Oval 1.01 ct', carat: 1.01, pricePerCt: 6200 },
        { sale: 'Sale 2', saleDate: xlsxUtcDate('2026-10-03'), customer: customers[1] ?? cust, terms: 60, keep: 2, broker: brokers[0] ?? '', brokerPct: brokers[0] ? 1 : '', code: '20003', stone: 'Pear 0.50 ct parcel', carat: 3, pricePerCt: 1400 },
      ],
    }],
    lists: { customers, brokers },
    freeLists: ['customers', 'brokers'],
    readme: [
      'How to import sales',
      'One row per stone. Rows with the same Sale, Customer and Sale Date make one sale invoice.',
      'Code must be a stone you have in stock. Carat to sell can be part of a parcel; the rest stays in stock.',
      'Payment Terms is in days. Leave it blank to use the customer\'s default terms.',
      'Keep (%) is the share the customer keeps for his own client. Leave it blank or 0 for none.',
      'Broker and Broker % are optional, per stone.',
      'On import each sale opens in the Add Sale form already filled in. Check it and click Save; the next one opens by itself.',
    ],
  });
}

export function ImportSalesModal({ open, onClose, entities, inventory, dummyId, onStart }: {
  open:      boolean;
  onClose:   () => void;
  entities:  Entity[];
  inventory: InventoryItem[];
  dummyId?:  string;
  onStart:   (groups: SaleImportGroup[]) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [groups,   setGroups]   = useState<SaleImportGroup[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error,    setError]    = useState('');
  const [busy,     setBusy]     = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [sampleBusy, setSampleBusy] = useState(false);

  function reset() { setFileName(''); setGroups([]); setWarnings([]); setError(''); }
  function close() { reset(); onClose(); }

  async function readFile(f: File) {
    setBusy(true); setError('');
    try {
      const wb = await loadWorkbook(await f.arrayBuffer());
      const warn: string[] = [];
      const rows = readSheetRows(wb, 'Sales', n => n.includes('sale'), SALE_COLS, warn)
        ?? readSheetRows(wb, wb.worksheets[0]?.name ?? '', () => false, SALE_COLS, warn) ?? [];
      if (rows.length === 0) throw new Error('No sale rows found. Use the sample file\'s columns.');

      const customers = entities.filter(e => e.types.includes('customer'));
      const brokers   = entities.filter(e => e.types.includes('broker'));
      const byName = (pool: Entity[], v: string) => pool.find(e => norm(e.companyName) === norm(v));

      const map = new Map<string, SaleImportGroup>();
      for (const r of rows) {
        const t = r.text;
        const where = `Row ${r.rowNo}`;
        const saleDate = parseImportDate(r.raw.saleDate ?? t.saleDate);
        const custName = t.customer ?? '';
        const ref = t.sale ?? '';
        const key = [norm(ref), norm(custName), saleDate].join('|');
        let g = map.get(key);
        if (!g) {
          const cust = byName(customers, custName);
          const termsTxt = cleanNum(t.terms ?? '');
          const keepTxt  = cleanNum(t.keep ?? '');
          g = {
            key, ref: ref || `Sale ${map.size + 1}`,
            customerId: cust?.id ?? '', customerName: cust?.companyName ?? custName,
            saleDate,
            terms: termsTxt ? Math.max(0, Math.round(+termsTxt)) : (cust?.defaultTerms ?? 30),
            keep:  keepTxt ? +keepTxt : (cust?.defaultLessPercent ?? 0),
            lines: [], problems: [],
          };
          if (!custName) g.problems.push('No customer');
          else if (!cust) g.problems.push(`Customer "${custName}" not found`);
          if (!saleDate) g.problems.push('No sale date');
          map.set(key, g);
        }
        const carat = parseFloat(cleanNum(t.carat ?? '')) || 0;
        let price = parseFloat(cleanNum(t.pricePerCt ?? '')) || 0;
        const amount = parseFloat(cleanNum(t.amount ?? '')) || 0;
        if (!price && amount && carat) price = r2(amount / carat);
        let brokerId: string | undefined;
        if (t.broker) {
          const b = byName(brokers, t.broker);
          if (b) brokerId = b.id; else warn.push(`${where}: broker "${t.broker}" not found, left blank.`);
        }
        const pct = parseFloat(cleanNum(t.brokerPct ?? ''));
        g.lines.push({
          rowNo: r.rowNo, code: (t.code ?? '').trim(), carat, pricePerCt: price,
          brokerId, brokerPct: brokerId && pct > 0 ? pct : undefined,
        });
      }

      // Check every line against stock, in the order the sales will be saved
      // (oldest first), so two sales taking from one parcel are counted together.
      const list = [...map.values()].sort((a, b) => (a.saleDate || '9999').localeCompare(b.saleDate || '9999'));
      const claimed = new Map<string, number>();
      for (const g of list) {
        for (const li of g.lines) {
          const at = `${li.code || `row ${li.rowNo}`}`;
          if (!li.code) { g.problems.push(`Row ${li.rowNo}: no Code`); continue; }
          const item = sellableByCode(inventory, li.code, dummyId);
          if (!item) { g.problems.push(`${at}: not in stock`); continue; }
          if (!(li.carat > 0)) g.problems.push(`${at}: no carat`);
          if (!(li.pricePerCt > 0)) g.problems.push(`${at}: no price`);
          if (item.id === dummyId) continue;
          const left = availableCarat(item) - (claimed.get(item.id) ?? 0);
          if (li.carat > left + 1e-9) g.problems.push(`${at}: only ${r2(Math.max(0, left))} ct left`);
          claimed.set(item.id, (claimed.get(item.id) ?? 0) + li.carat);
        }
      }
      setFileName(f.name);
      setGroups(list);
      setWarnings(warn);
    } catch (e) {
      console.error('[Sales import] read failed:', e);
      setError(e instanceof Error && e.message ? e.message : 'Could not read that file.');
    }
    setBusy(false);
  }

  async function downloadSample() {
    setSampleBusy(true);
    try { await downloadSalesImportSample(entities); } finally { setSampleBusy(false); }
  }

  const totals = useMemo(() => {
    let ct = 0, amt = 0, lines = 0;
    for (const g of groups) for (const li of g.lines) { ct += li.carat; amt += li.carat * li.pricePerCt; lines++; }
    return { ct, amt, lines, bad: groups.filter(g => g.problems.length > 0).length };
  }, [groups]);

  const th = { position: 'sticky' as const, top: 0, zIndex: 1, textAlign: 'left' as const, whiteSpace: 'nowrap' as const };
  const td = { fontSize: 12, color: 'var(--text-2)', whiteSpace: 'nowrap' as const };
  const num = { ...td, fontFamily: 'var(--font-num)', textAlign: 'right' as const };
  const fmt = (n: number, d = 2) => n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

  return (
    <Modal
      open={open}
      onClose={close}
      title="Import Sales"
      subtitle={fileName || 'From an Excel file'}
      size={groups.length ? 'xl' : 'md'}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
          {groups.length > 0 && (
            <span style={{ marginRight: 'auto', fontSize: 12, color: 'var(--text-3)' }}>
              {groups.length} sale{groups.length !== 1 ? 's' : ''} · {totals.lines} item{totals.lines !== 1 ? 's' : ''} · {fmt(totals.ct)} ct · ${fmt(totals.amt)}
            </span>
          )}
          {groups.length > 0 && <Button variant="secondary" onClick={reset}>Choose Another File</Button>}
          <Button variant="secondary" onClick={close}>Cancel</Button>
          {groups.length > 0 && (
            <Button variant="primary" onClick={() => { const g = groups; reset(); onStart(g); }}>
              Start ({groups.length} sale{groups.length !== 1 ? 's' : ''})
            </Button>
          )}
        </div>
      }
    >
      <input ref={fileRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) readFile(f); }} />

      {error && <div className="msg msg-error" style={{ marginBottom: 10 }}>{error}</div>}

      {groups.length === 0 ? (
        <>
          <div
            role="button" tabIndex={0}
            onClick={() => fileRef.current?.click()}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileRef.current?.click(); } }}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) readFile(f); }}
            style={{
              border: `1.5px dashed ${dragOver ? 'var(--primary)' : 'var(--border)'}`,
              background: dragOver ? 'var(--primary-light)' : 'var(--surface-2)',
              borderRadius: 'var(--r-sm)', padding: '26px 16px', textAlign: 'center', cursor: 'pointer',
              color: 'var(--text-3)', transition: 'background 0.15s, border-color 0.15s',
            }}
          >
            <IconUploadFile />
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', marginTop: 6 }}>
              {busy ? 'Reading…' : 'Choose or drop an Excel file'}
            </div>
            <div style={{ fontSize: 11.5, marginTop: 2 }}>.xlsx · one row per stone, one or many sales</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="line-item-link-btn" onClick={downloadSample} disabled={sampleBusy}>
              {sampleBusy ? 'Preparing…' : 'Download Sample File'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="msg-note" style={{ marginBottom: 10 }}>
            Each sale opens in the Add Sale form already filled in. Check it and click Save; the next one opens by itself.
          </div>
          {totals.bad > 0 && (
            <div className="msg msg-error" style={{ marginBottom: 10 }}>
              {totals.bad} sale{totals.bad !== 1 ? 's have' : ' has'} something to fix (in red). You can still start and fix {totals.bad !== 1 ? 'them' : 'it'} in the form, or Skip.
            </div>
          )}
          <div style={{ border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'auto', maxHeight: '52vh', overscrollBehavior: 'contain' }}>
            <table className="excel-grid-table">
              <thead>
                <tr>
                  <th style={th}>#</th>
                  <th style={th}>Sale</th>
                  <th style={{ ...th, minWidth: 200 }}>Customer</th>
                  <th style={th}>Date</th>
                  <th style={th}>Terms</th>
                  <th style={th} title="Keep (%)">Keep</th>
                  <th style={{ ...th, textAlign: 'right' }}>Items</th>
                  <th style={{ ...th, textAlign: 'right' }}>Carat</th>
                  <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                  <th style={{ ...th, minWidth: 220 }}>Check</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g, i) => {
                  const ct  = g.lines.reduce((s, l) => s + l.carat, 0);
                  const amt = g.lines.reduce((s, l) => s + l.carat * l.pricePerCt, 0);
                  const bad = g.problems.length > 0;
                  return (
                    <tr key={g.key} style={bad ? { background: 'color-mix(in srgb, var(--danger) 7%, transparent)' } : undefined}>
                      <td style={num}>{i + 1}</td>
                      <td style={td}>{g.ref}</td>
                      <td style={{ ...td, color: g.customerId ? 'var(--text-2)' : 'var(--danger)' }}>{g.customerName || <span style={{ opacity: 0.4 }}>—</span>}</td>
                      <td style={{ ...td, fontFamily: 'var(--font-num)', color: g.saleDate ? 'var(--text-2)' : 'var(--danger)' }}>{g.saleDate || '—'}</td>
                      <td style={num}>{g.terms}d</td>
                      <td style={num}>{g.keep ? `${g.keep}%` : <span style={{ opacity: 0.4 }}>—</span>}</td>
                      <td style={num}>{g.lines.length}</td>
                      <td style={num}>{fmt(ct)}</td>
                      <td style={num}>${fmt(amt)}</td>
                      <td style={{ ...td, whiteSpace: 'normal', color: bad ? 'var(--danger)' : 'var(--success)', fontWeight: 600 }}>
                        {bad ? g.problems.join(' · ') : 'Ready'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {warnings.length > 0 && (
            <div className="msg-note" style={{ marginTop: 10, display: 'block' }}>
              {warnings.slice(0, 8).map((w, i) => <div key={i}>{w}</div>)}
              {warnings.length > 8 && <div>…and {warnings.length - 8} more.</div>}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
